import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../primitives';
import { docketSchema } from './docketSchema';

/**
 * A kitchen docket published to the phone docket viewer, shown there until someone clears it. How
 * it got there depends on the venue's viewer mode (`DocketViewerMode`): one that didn't print
 * (waiting in the primary POS's failed dockets list), or, at a venue with the docket viewer printer,
 * every docket sent to it. Keyed by the docket's own `uuid`, uploaded by the primary POS.
 *
 * Handled on either side (printed again or removed on the POS, cleared on the phone) it gets
 * `clearedAt`, a tombstone rather than a delete: a late re-upload from the POS can then never bring
 * back a docket the kitchen already cleared. Rows are deleted 24 hours after they are created.
 */
export const viewerDocketSchema = docketSchema
  .pick({ desiredPosPrinterIdentifier: true, docketType: true, localCreatedAt: true, uuid: true })
  .extend({
    /** Required, unlike on a docket: the viewer renders from it (`DocketPreview`). */
    docketPayload: z.string(),
    /** When the POS or the phone handled it. The first clear wins. */
    clearedAt: z.number().nullable(),
    /** Set by the server from the device's link, never sent by the POS. */
    restaurantId: z.string().optional(),
    _creationTime: z.number().optional(),
    _id: z.string().optional(),
  });
export type ViewerDocket = z.output<typeof viewerDocketSchema>;

export const viewerDocketStrictSchema = viewerDocketSchema.safeExtend({
  uuid: z.guid(),
});

export const viewerDocketCvxSchema = viewerDocketSchema.safeExtend({
  restaurantId: z.string(),
  _creationTime: z.number(),
  _id: z.string(),
});
export type ViewerDocketCvx = z.output<typeof viewerDocketCvxSchema>;
assertTypes<NoNewFields<ViewerDocket, ViewerDocketCvx>>();
